import React, { useEffect, useMemo, useState } from "react";
import { MessageSquare, Send, X, Loader2, CheckCircle2 } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { submitFeedback } from "../services/feedbackService";
import { errorDev } from '../utils/logger.js';

function findKnownIpAddress() {
  try {
    const keys = ["user_ip", "userIp", "ipAddress", "ip_address"];
    for (const key of keys) {
      const value = localStorage.getItem(key);
      if (value && String(value).trim()) return String(value).trim();
    }
  } catch (err) {
    // Ignore unavailable localStorage.
  errorDev("Operation failed:", err);
  }

  if (typeof window !== "undefined") {
    const candidates = [window.__USER_IP__, window.__IP_ADDRESS__, window.userIp];
    for (const value of candidates) {
      if (value && String(value).trim()) return String(value).trim();
    }
  }

  return null;
}

function FeedbackModal({ isOpen, onClose, source = "manual", user }) {
  const [topic, setTopic] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setTopic("");
    setMessage("");
    setSubmitting(false);
    setSubmitted(false);
    setError("");
  }, [isOpen]);

  if (!isOpen) return null;

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!message.trim()) {
      setError("Please share your feedback message.");
      return;
    }

    if (!user) {
      setError("Please log in to send feedback.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      await submitFeedback(user, {
        category: "orrith_feedback",
        topic: topic.trim(),
        message: message.trim(),
        source: "orrith_feedback_button",
        ipAddress: findKnownIpAddress(),
        metadata: {
          page: window.location.pathname,
        },
      });

      setSubmitted(true);
      setTimeout(() => onClose(), 1800);
    } catch (err) {
      setError(err?.message || "Failed to send feedback.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] p-4 flex items-center justify-center" role="dialog" aria-modal="true" aria-label="Feedback form">
      <button className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={onClose} aria-label="Close feedback form" />

      <div className="relative w-full max-w-md rounded-2xl border border-emerald-300/30 dark:border-emerald-500/20 bg-white dark:bg-neutral-900 shadow-[0_16px_42px_rgba(0,0,0,0.34)] overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-neutral-800 flex items-center justify-between bg-gradient-to-r from-emerald-700 to-green-600 dark:from-emerald-900 dark:to-green-800 text-white">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-lg bg-white/15 border border-white/20 flex items-center justify-center">
              <MessageSquare className="w-4 h-4" />
            </span>
            <div>
              <p className="text-sm font-semibold leading-tight">Orrith Feedback</p>
              <p className="text-[11px] text-emerald-100">Cycle of Certainty</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/10 border border-white/20 hover:bg-white/20 transition" aria-label="Close">
            <X className="w-4 h-4 mx-auto" />
          </button>
        </div>

        {submitted ? (
          <div className="px-5 py-8 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/35 text-emerald-700 dark:text-emerald-400 mx-auto mb-3 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              Thank you — your feedback helps make Orrith better.
            </p>
          </div>
        ) : (
          <form className="px-5 py-4 space-y-3" onSubmit={onSubmit}>
            <div>
              <label htmlFor="orrith-feedback-topic" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                Topic
              </label>
              <input
                id="orrith-feedback-topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="Short topic"
                maxLength={120}
                className="w-full rounded-xl border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-800 text-sm text-gray-900 dark:text-white px-3 py-2.5 outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label htmlFor="orrith-feedback-message" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                Message
              </label>
              <textarea
                id="orrith-feedback-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Share your feedback"
                rows={4}
                maxLength={2000}
                required
                className="w-full rounded-xl border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-800 text-sm text-gray-900 dark:text-white px-3 py-2.5 outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
              />
            </div>

            {error && (
              <p className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/25 border border-red-200 dark:border-red-800 rounded-lg px-2.5 py-2">
                {error}
              </p>
            )}

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 h-10 rounded-xl border border-gray-200 dark:border-neutral-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-800 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !message.trim()}
                className="flex-1 h-10 rounded-xl text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed transition inline-flex items-center justify-center gap-1.5"
              >
                {submitting ? <span className="orrith-loader orrith-loader-xs orrith-loader-on-dark" /> : <Send className="w-4 h-4" />}
                Submit
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function OrrithFeedbackSystem({ mode = "floating", className = "" }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("orrith-feedback-open", onOpen);
    return () => window.removeEventListener("orrith-feedback-open", onOpen);
  }, []);

  const buttonClass = useMemo(() => {
    if (mode === "inline") {
      return `w-full rounded-xl px-4 py-3 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 hover:border-emerald-500 dark:hover:border-emerald-600 transition text-sm font-medium text-gray-800 dark:text-gray-100 inline-flex items-center justify-center gap-2 ${className}`;
    }

    return `hidden md:inline-flex fixed right-4 bottom-6 z-[54] rounded-full px-3.5 py-2.5 bg-green-700 hover:bg-green-800 text-white shadow-[0_10px_24px_rgba(0,0,0,0.3)] border border-green-500/55 transition items-center gap-2 ${className}`;
  }, [mode, className]);

  return (
    <>
      <button
        type="button"
        className={buttonClass}
        onClick={() => setOpen(true)}
        aria-label="Open feedback form"
      >
        <MessageSquare className="w-4 h-4" />
        <span>Feedback</span>
      </button>

      <FeedbackModal isOpen={open} onClose={() => setOpen(false)} source="orrith_feedback_button" user={user} />
    </>
  );
}
